import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Filler,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { Chart, Doughnut, Line } from 'react-chartjs-2';

import { PageHeading } from '../components/common/PageHeading';
import { get } from '../lib/api';
import { usePageTitle } from '../lib/usePageTitle';

ChartJS.register(
    ArcElement,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Filler,
);

// Consistent palette across every chart: users = orange (always the LEFT axis on the
// time series), requests = blue (always the RIGHT axis).
const C_USERS = '#fd7e14';
const C_USERS_PARTIAL = 'rgba(253,126,20,.4)'; // dagens (delvise) søyle — lysere
const C_CALLS = '#0d6efd';
const C_JASPER = '#20c997';
const C_OTHER = '#adb5bd'; // "Øvrig" — nøytral rest av trafikken
const C_OFFICE = '#6f42c1';
const C_TICK = '#6c757d';
const C_GRID = 'rgba(128,128,128,.18)';

interface AppTypeUsage {
    type: string;
    users: number;
    requests: number;
}
interface TimePoint {
    label: string;
    users: number;
    requests: number;
}
interface AdminStats {
    telemetryAvailable: boolean;
    activeUsersToday: number | null;
    appTypes: AppTypeUsage[];
    hourly: TimePoint[];
    daily: TimePoint[];
    unavailableReason?: string | null;
    generatedAt: string;
}
interface AppMapping {
    name: string;
    type: string;
    users: number;
}
interface MappingStats {
    totalUsers: number;
    usersWithMapping: number;
    usersWithoutMapping: number;
    asp: { users: number; apps: number };
    applications: AppMapping[];
}

export function AdminStatsPage() {
    const { t, i18n } = useTranslation();
    usePageTitle(t('admin.stats.title'));

    const [stats, setStats] = useState<AdminStats | null>(null);
    const [mapping, setMapping] = useState<MappingStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(false);
        // Telemetry stats (App Insights) and mapping stats (DB) are independent — the mapping
        // section shows even if telemetry is unavailable.
        const a = get<AdminStats>('home/GetAdminStats').then((r) => setStats(r.data));
        const m = get<MappingStats>('home/GetMappingStats')
            .then((r) => setMapping(r.data))
            .catch(() => setMapping(null));
        a.catch(() => setError(true));
        void Promise.allSettled([a, m]).then(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'nb-NO';
    const nf = useMemo(() => new Intl.NumberFormat(locale), [locale]);

    const usersLabel = t('admin.stats.seriesUsers');
    const callsLabel = t('admin.stats.seriesCalls');

    // Shared dual-axis config: users on the left (orange), requests on the right (blue).
    const dualAxes = {
        x: { grid: { display: false }, ticks: { color: C_TICK, font: { size: 11 }, maxRotation: 0 } },
        y: { beginAtZero: true, position: 'left' as const, grid: { color: C_GRID }, ticks: { color: C_USERS, font: { size: 11 }, precision: 0 } },
        y1: {
            beginAtZero: true,
            position: 'right' as const,
            grid: { display: false },
            ticks: {
                color: C_CALLS,
                font: { size: 11 },
                precision: 0,
                callback: (v: string | number) => (Number(v) >= 1000 ? `${Number(v) / 1000}k` : `${v}`),
            },
        },
    };

    const donut = useMemo(() => {
        if (!stats) return null;
        const total = stats.appTypes.reduce((s, a) => s + a.requests, 0) || 1;
        // "Other" last; app types by requests desc — a self-reconciling composition donut.
        const ordered = [...stats.appTypes].sort(
            (a, b) => (a.type === 'Other' ? 1 : 0) - (b.type === 'Other' ? 1 : 0) || b.requests - a.requests,
        );
        const colorOf = (type: string) => (type === 'Other' ? C_OTHER : type === 'Jasper' ? C_JASPER : C_CALLS);
        const data: ChartData<'doughnut'> = {
            labels: ordered.map((a) => (a.type === 'Other' ? t('admin.stats.otherType') : a.type)),
            datasets: [
                {
                    data: ordered.map((a) => a.requests),
                    backgroundColor: ordered.map((a) => colorOf(a.type)),
                    borderWidth: 0,
                },
            ],
        };
        const options: ChartOptions<'doughnut'> = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '72%',
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (c) => ` ${c.label}: ${nf.format(Number(c.raw))} (${((Number(c.raw) / total) * 100).toFixed(1)} %)`,
                    },
                },
            },
        };
        return { data, options };
    }, [stats, nf, t]);

    const hourly = useMemo(() => {
        if (!stats) return null;
        const data: ChartData<'line'> = {
            labels: stats.hourly.map((p) => p.label),
            datasets: [
                { label: usersLabel, data: stats.hourly.map((p) => p.users), borderColor: C_USERS, backgroundColor: C_USERS, tension: 0.35, pointRadius: 0, borderWidth: 2, yAxisID: 'y' },
                { label: callsLabel, data: stats.hourly.map((p) => p.requests), borderColor: C_CALLS, backgroundColor: 'rgba(13,110,253,.14)', fill: true, tension: 0.35, pointRadius: 0, borderWidth: 2, yAxisID: 'y1' },
            ],
        };
        const options: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${nf.format(Number(c.parsed.y))}` } } },
            scales: dualAxes,
        };
        return { data, options };
    }, [stats, nf, usersLabel, callsLabel]);

    const monthly = useMemo(() => {
        if (!stats) return null;
        const now = new Date();
        const todayLabel = String(now.getDate());
        const data = {
            labels: stats.daily.map((p) => p.label),
            datasets: [
                { type: 'bar' as const, label: usersLabel, data: stats.daily.map((p) => p.users), backgroundColor: stats.daily.map((p) => (p.label === todayLabel ? C_USERS_PARTIAL : C_USERS)), borderRadius: 3, yAxisID: 'y', order: 2 },
                { type: 'line' as const, label: callsLabel, data: stats.daily.map((p) => p.requests), borderColor: C_CALLS, backgroundColor: C_CALLS, tension: 0.3, pointRadius: 0, borderWidth: 2, yAxisID: 'y1', order: 1 },
            ],
        };
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items) => new Date(now.getFullYear(), now.getMonth(), Number(items[0].label)).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
                        label: (c) => ` ${c.dataset.label}: ${nf.format(Number(c.parsed.y))}`,
                    },
                },
            },
            scales: dualAxes,
        };
        return { data, options };
    }, [stats, nf, locale, usersLabel, callsLabel]);

    const mappingDonut = useMemo(() => {
        if (!mapping) return null;
        const data: ChartData<'doughnut'> = {
            labels: [t('admin.stats.withMapping'), t('admin.stats.withoutMapping')],
            datasets: [{ data: [mapping.usersWithMapping, mapping.usersWithoutMapping], backgroundColor: [C_CALLS, C_OTHER], borderWidth: 0 }],
        };
        const options: ChartOptions<'doughnut'> = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${c.label}: ${nf.format(Number(c.raw))}` } } },
        };
        return { data, options };
    }, [mapping, nf, t]);

    const appTotal = (stats?.appTypes ?? []).reduce((sum, a) => sum + a.requests, 0) || 1;
    const orderedTypes = [...(stats?.appTypes ?? [])].sort(
        (a, b) => (a.type === 'Other' ? 1 : 0) - (b.type === 'Other' ? 1 : 0) || b.requests - a.requests,
    );
    const topType = orderedTypes.find((a) => a.type !== 'Other');
    const updatedAt = stats?.generatedAt
        ? new Date(stats.generatedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
        : '';

    // Usage: unique active users today vs total registered users (color by threshold).
    const totalRegistered = mapping?.totalUsers ?? null;
    const usagePct =
        totalRegistered && stats?.activeUsersToday != null && totalRegistered > 0
            ? Math.round((stats.activeUsersToday / totalRegistered) * 100)
            : null;
    const usageColor = usagePct == null ? C_TICK : usagePct < 25 ? '#dc3545' : usagePct <= 50 ? '#f59f00' : '#198754';

    // Mapping distribution view.
    const mapColorOf = (type: string) => (type === 'Jasper' ? C_JASPER : type === 'Office365' ? C_OFFICE : C_CALLS);
    const topApps = (mapping?.applications ?? []).slice(0, 12);
    const topAppsMax = Math.max(1, ...topApps.map((a) => a.users));

    function legend(color: string, label: string, line = false) {
        return (
            <span className="d-flex align-items-center gap-1">
                <span style={{ width: line ? 14 : 11, height: line ? 3 : 11, borderRadius: line ? 2 : 3, background: color, display: 'inline-block' }} />
                {label}
            </span>
        );
    }
    const seriesLegend = (
        <div className="d-flex gap-3 small text-body-secondary">
            {legend(C_USERS, usersLabel)}
            {legend(C_CALLS, callsLabel, true)}
        </div>
    );

    return (
        <>
            <PageHeading
                title={t('admin.stats.title')}
                description={t('admin.stats.description')}
                meta={
                    !loading && !error && stats?.telemetryAvailable && updatedAt ? (
                        <span className="small">
                            {t('admin.stats.updatedAt', { time: updatedAt })}
                            <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={load}>
                                {t('admin.stats.refresh')}
                            </Button>
                        </span>
                    ) : undefined
                }
            />

            {loading && (
                <div className="text-body-secondary d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> {t('admin.stats.loading')}
                </div>
            )}

            {!loading && error && (
                <Alert variant="danger" className="mb-0">
                    {t('admin.stats.error')}
                    <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={load}>
                        {t('admin.stats.refresh')}
                    </Button>
                </Alert>
            )}

            {!loading && !error && stats && !stats.telemetryAvailable && (
                <Alert variant="info">
                    {t('admin.stats.unavailable')}
                    {stats.unavailableReason && (
                        <div className="small text-body-secondary mt-2 font-monospace">{stats.unavailableReason}</div>
                    )}
                </Alert>
            )}

            {!loading && !error && stats?.telemetryAvailable && (
                <>
                    <Row className="g-3 mb-3">
                        <Col md={5}>
                            <Card body className="shadow-sm h-100">
                                <div className="text-body-secondary">{t('admin.stats.activeToday')}</div>
                                <div className="display-5 fw-semibold mt-1">
                                    {stats.activeUsersToday != null ? nf.format(stats.activeUsersToday) : '–'}
                                </div>
                                {usagePct != null && (
                                    <div className="mt-2">
                                        <div style={{ height: 9, background: 'rgba(128,128,128,.2)', borderRadius: 5, overflow: 'hidden' }}>
                                            <div style={{ width: `${usagePct}%`, height: '100%', background: usageColor, borderRadius: 5 }} />
                                        </div>
                                        <div className="small mt-1">
                                            <span style={{ color: usageColor, fontWeight: 500 }}>{t('admin.stats.usagePct', { pct: usagePct })}</span>
                                            <span className="text-body-secondary"> · {t('admin.stats.usageOf', { active: nf.format(stats.activeUsersToday ?? 0), total: nf.format(totalRegistered ?? 0) })}</span>
                                        </div>
                                    </div>
                                )}
                                <div className="text-body-secondary small mt-2">{t('admin.stats.activeTodayHint')}</div>
                            </Card>
                        </Col>
                        <Col md={7}>
                            <Card body className="shadow-sm h-100">
                                <div className="text-body-secondary mb-2">{t('admin.stats.mostUsedType')}</div>
                                {topType && donut ? (
                                    <div className="d-flex align-items-center gap-3">
                                        <div style={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
                                            <Doughnut data={donut.data} options={donut.options} />
                                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                                <div className="fw-semibold" style={{ fontSize: '0.95rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{topType.type}</div>
                                                <div className="small text-body-secondary">{t('admin.stats.mostUsedBadge')}</div>
                                            </div>
                                        </div>
                                        <div className="small">
                                            {orderedTypes.map((a) => {
                                                const isOther = a.type === 'Other';
                                                const pct = Math.round((a.requests / appTotal) * 100);
                                                return (
                                                    <div key={a.type} className="mb-2">
                                                        <div className="d-flex align-items-center gap-1 fw-semibold">
                                                            <span style={{ width: 11, height: 11, borderRadius: 3, background: isOther ? C_OTHER : a.type === 'Jasper' ? C_JASPER : C_CALLS, display: 'inline-block' }} />
                                                            {isOther ? t('admin.stats.otherType') : a.type}
                                                        </div>
                                                        <div className="text-body-secondary ms-3">
                                                            {isOther
                                                                ? t('admin.stats.legendOther', { requests: nf.format(a.requests), pct })
                                                                : t('admin.stats.legendApp', { requests: nf.format(a.requests), pct, users: nf.format(a.users) })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-body-secondary small">{t('admin.stats.noUsage')}</div>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {hourly && (
                        <Card body className="shadow-sm mb-3">
                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                                <div className="text-body-secondary">{t('admin.stats.hourlyTitle')}</div>
                                {seriesLegend}
                            </div>
                            <div style={{ position: 'relative', height: 170 }}>
                                <Line data={hourly.data} options={hourly.options} />
                            </div>
                        </Card>
                    )}

                    {monthly && (
                        <Card body className="shadow-sm">
                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                                <div className="text-body-secondary">{t('admin.stats.monthlyTitle')}</div>
                                {seriesLegend}
                            </div>
                            <div style={{ position: 'relative', height: 165 }}>
                                <Chart type="bar" data={monthly.data as unknown as ChartData<'bar'>} options={monthly.options} />
                            </div>
                            <div className="text-body-tertiary small mt-2">{t('admin.stats.monthlyNote')}</div>
                        </Card>
                    )}
                </>
            )}

            {!loading && !error && mapping && (
                <Card body className="shadow-sm mt-3">
                    <div className="fw-semibold mb-1">{t('admin.stats.mappingTitle')}</div>
                    <div className="text-body-secondary small mb-3">{t('admin.stats.mappingDesc')}</div>

                    <div className="d-flex align-items-center gap-3 flex-wrap pb-3 mb-3 border-bottom">
                        {mappingDonut && (
                            <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
                                <Doughnut data={mappingDonut.data} options={mappingDonut.options} />
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                    <div className="fw-semibold">{nf.format(mapping.totalUsers)}</div>
                                    <div className="small text-body-secondary">{t('admin.stats.usersWord')}</div>
                                </div>
                            </div>
                        )}
                        <div className="small">
                            <div className="d-flex align-items-center gap-1">
                                <span style={{ width: 11, height: 11, borderRadius: 3, background: C_CALLS, display: 'inline-block' }} />
                                {t('admin.stats.withMapping')} {nf.format(mapping.usersWithMapping)}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                                <span style={{ width: 11, height: 11, borderRadius: 3, background: C_OTHER, display: 'inline-block' }} />
                                {t('admin.stats.withoutMapping')} {nf.format(mapping.usersWithoutMapping)}
                            </div>
                        </div>
                    </div>

                    {mapping.asp.apps > 0 && (
                        <div className="mb-3 p-2 px-3 rounded" style={{ background: 'rgba(13,110,253,.08)', border: '1px solid rgba(13,110,253,.25)' }}>
                            <div className="d-flex justify-content-between align-items-baseline gap-2">
                                <span className="fw-semibold small">
                                    {t('admin.stats.aspAggregate')} <span className="text-body-secondary fw-normal">(192.168.168.213)</span>
                                </span>
                                <span className="fw-semibold">{nf.format(mapping.asp.users)}</span>
                            </div>
                            <div className="text-body-secondary" style={{ fontSize: '0.72rem' }}>
                                {t('admin.stats.aspNote', { apps: mapping.asp.apps })}
                            </div>
                        </div>
                    )}

                    <div className="fw-semibold small mb-2">{t('admin.stats.singleApps')}</div>
                    {topApps.map((a) => {
                        const pct = Math.round((a.users / topAppsMax) * 100);
                        return (
                            <div key={a.name} className="mb-2">
                                <div className="d-flex justify-content-between align-items-baseline gap-2 small mb-1">
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {a.name} <span className="text-body-tertiary">· {a.type}</span>
                                    </span>
                                    <span className="fw-semibold flex-shrink-0">{nf.format(a.users)}</span>
                                </div>
                                <div style={{ height: 8, background: 'rgba(128,128,128,.15)', borderRadius: 4, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: mapColorOf(a.type), borderRadius: 4 }} />
                                </div>
                            </div>
                        );
                    })}
                    <div className="text-body-tertiary small mt-2">
                        {t('admin.stats.topNote', { shown: topApps.length, total: mapping.applications.length })}
                    </div>
                </Card>
            )}
        </>
    );
}
